import { useState, useRef, useEffect } from "react";
import { Pencil } from "lucide-react";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

interface InlineEditFieldProps {
  value: string;
  onSave: (value: string) => void;
  placeholder?: string;
  multiline?: boolean;
  className?: string;
  displayClassName?: string;
  inputClassName?: string;
  emptyPlaceholder?: string;
  required?: boolean;
  requiredMessage?: string;
  onValidationError?: (message: string) => void;
}

export function InlineEditField({
  value,
  onSave,
  placeholder,
  multiline = false,
  className,
  displayClassName,
  inputClassName,
  emptyPlaceholder = "Click to add...",
  required = false,
  requiredMessage = "This field is required.",
  onValidationError,
}: InlineEditFieldProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
    }
  }, [editing]);

  const handleSave = () => {
    const trimmed = draft.trim();
    if (required && !trimmed) {
      setDraft(value);
      setEditing(false);
      onValidationError?.(requiredMessage);
      return;
    }
    setEditing(false);
    if (trimmed !== value.trim()) onSave(trimmed);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !multiline) {
      e.preventDefault();
      handleSave();
    }
    if (e.key === "Enter" && multiline && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleSave();
    }
    if (e.key === "Escape") {
      setDraft(value);
      setEditing(false);
    }
  };

  if (editing) {
    if (multiline) {
      return (
        <div className={className}>
          <Textarea
            ref={inputRef as React.RefObject<HTMLTextAreaElement>}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={handleSave}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className={cn("min-h-[80px] resize-y w-full", inputClassName)}
            autoFocus
          />
        </div>
      );
    }
    return (
      <div className={className}>
        <Input
          ref={inputRef as React.RefObject<HTMLInputElement>}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className={cn("w-full", inputClassName)}
          autoFocus
        />
      </div>
    );
  }

  return (
    <div
      className={cn("group flex items-center gap-1 cursor-text min-h-[1.5rem]", className)}
      onClick={() => {
        setDraft(value);
        setEditing(true);
      }}
    >
      <span
        className={cn(
          "flex-1",
          !value && "text-muted-foreground italic text-sm",
          displayClassName
        )}
      >
        {value || emptyPlaceholder}
      </span>
      <Pencil className="w-3 h-3 text-muted-foreground opacity-0 group-hover:opacity-60 transition-opacity shrink-0" />
    </div>
  );
}
